import { mapActions, mapMutations, mapState } from 'vuex'

export function typedMapActions<T extends string> (namespace: string, actions: Array<T>) {
  return mapActions(namespace, actions)
}

export function typedMapMutations<T extends string> (namespace: string, mutations: Array<T>) {
  return mapMutations(namespace, mutations)
}

export function typedMapState<T extends string> (namespace: string, states: Array<T>) {
  return mapState(namespace, states)
}

interface FetchWithAbortOptions<K extends string, TResponse> {
  commit: (type: string, payload?: unknown) => void
  state: Record<K, AbortController | null>
  abortControllerKey: K
  abortControllerMutationType: string
  pendingMutationType: string
  request: (signal: AbortSignal) => Promise<TResponse>
  onSuccess: (response: TResponse) => void | Promise<void>
}

function isAbortError (error: unknown): boolean {
  return error instanceof Error && (error.name === 'AbortError' || error.name === 'CanceledError')
}

// Cancels the previous in-flight request for the same flow before starting a new one,
// so a slower earlier response can never overwrite the result of a more recent request.
export async function fetchWithAbort<K extends string, TResponse> ({
  commit,
  state,
  abortControllerKey,
  abortControllerMutationType,
  pendingMutationType,
  request,
  onSuccess,
}: FetchWithAbortOptions<K, TResponse>): Promise<void> {
  state[abortControllerKey]?.abort()

  const abortController = new AbortController()
  commit(abortControllerMutationType, abortController)
  commit(pendingMutationType, true)

  try {
    const response = await request(abortController.signal)
    await onSuccess(response)
  } catch (error) {
    if (isAbortError(error)) {
      return
    }
    console.error(error)
    throw error
  } finally {
    if (state[abortControllerKey] === abortController) {
      commit(abortControllerMutationType, null)
      commit(pendingMutationType, false)
    }
  }
}
